import axiosClient from './axiosClient.js';
import {
  normalizeDemoReset,
  normalizeAssetMetadata,
  normalizeGuardianAnalysis,
  normalizeJournalDetail,
  normalizeJournalList,
  normalizeLedger,
  normalizeStrategies,
} from './journalContracts.mjs';

export class JournalApiError extends Error {
  constructor(message, { status = null, code = null, data = null, headers = null, cause = null } = {}) {
    super(message);
    this.name = 'JournalApiError';
    this.status = status;
    this.code = code;
    this.data = data;
    this.headers = headers;
    this.cause = cause;
    this.isConflict = status === 409;
    this.isCanceled = code === 'ERR_CANCELED';
  }
}

const requireAccountId = (accountId) => {
  if (accountId === null || accountId === undefined || accountId === '') {
    throw new TypeError('account_id is required for Journal requests.');
  }
  return String(accountId);
};

const wrapError = (error, fallbackMessage) => {
  if (error instanceof JournalApiError) return error;
  const status = error?.response?.status ?? null;
  const data = error?.response?.data ?? null;
  const detail = typeof data?.detail === 'string' ? data.detail : null;
  const message = detail || (status ? `${fallbackMessage} (HTTP ${status})` : error?.message || fallbackMessage);
  return new JournalApiError(message, {
    status,
    code: error?.code ?? data?.code ?? null,
    data,
    headers: error?.response?.headers ?? null,
    cause: error,
  });
};

const request = async (config, fallbackMessage) => {
  try {
    return await axiosClient({ ...config, skipGlobalErrorHandler: true });
  } catch (error) {
    throw wrapError(error, fallbackMessage);
  }
};

const entryPath = (journalEntryId, suffix = '') => {
  if (journalEntryId === null || journalEntryId === undefined || journalEntryId === '') {
    throw new TypeError('journal_entry_id is required.');
  }
  return `/journal/journalentries/${encodeURIComponent(String(journalEntryId))}/${suffix}`;
};

export const fetchJournalList = async ({ accountId, status = 'all', search, ordering = '-opened_at', from, to, page = 1, pageSize = 100, signal } = {}) => {
  const response = await request(
    {
      method: 'GET',
      url: '/journal/journalentries/',
      params: {
        account_id: requireAccountId(accountId),
        status,
        search: search || undefined,
        ordering,
        from: from || undefined,
        to: to || undefined,
        include: 'guardian_summary,asset',
        page,
        page_size: pageSize,
      },
      signal,
    },
    'Failed to load Journal entries'
  );
  return normalizeJournalList(response.data);
};

export const fetchJournalDetail = async ({ accountId, journalEntryId, signal } = {}) => {
  const response = await request(
    {
      method: 'GET',
      url: entryPath(journalEntryId),
      params: { account_id: requireAccountId(accountId) },
      signal,
    },
    'Failed to load Journal detail'
  );
  const entry = normalizeJournalDetail(response.data);
  if (entry.id !== String(journalEntryId)) throw new TypeError('Journal detail identity did not match the requested entry.');
  return { ...entry, etag: response.headers?.etag ?? null };
};

export const fetchGuardianAnalysis = async ({ accountId, journalEntryId, signal } = {}) => {
  const response = await request(
    {
      method: 'GET',
      url: entryPath(journalEntryId, 'guardian-analysis/'),
      params: { account_id: requireAccountId(accountId) },
      signal,
    },
    'Failed to load Guardian Analysis'
  );
  const analysis = normalizeGuardianAnalysis(response.data);
  if (analysis.journalEntryId && analysis.journalEntryId !== String(journalEntryId)) {
    throw new TypeError('Guardian Analysis identity did not match the requested entry.');
  }
  return analysis;
};

export const fetchJournalLedger = async ({ accountId, journalEntryId, signal } = {}) => {
  const response = await request(
    {
      method: 'GET',
      url: entryPath(journalEntryId, 'ledger/'),
      params: { account_id: requireAccountId(accountId) },
      signal,
    },
    'Failed to load the Journal ledger'
  );
  const ledger = normalizeLedger(response.data);
  if (ledger.journalEntryId && ledger.journalEntryId !== String(journalEntryId)) {
    throw new TypeError('Ledger identity did not match the requested entry.');
  }
  return ledger;
};

export const updateJournalEntry = async ({ accountId, journalEntryId, version, etag, patch, signal } = {}) => {
  const payload = { ...(version === null || version === undefined ? {} : { version }), ...(patch || {}) };
  const concurrencyToken = etag ?? (version === null || version === undefined ? null : String(version));
  const response = await request(
    {
      method: 'PATCH',
      url: entryPath(journalEntryId),
      params: { account_id: requireAccountId(accountId) },
      data: payload,
      headers: concurrencyToken ? { 'If-Match': concurrencyToken } : undefined,
      signal,
    },
    'Failed to save Journal metadata'
  );
  return { ...normalizeJournalDetail(response.data), etag: response.headers?.etag ?? null };
};

export const fetchJournalStrategies = async ({ accountId, signal } = {}) => {
  const response = await request(
    {
      method: 'GET',
      url: '/journal/strategies/',
      params: { account_id: requireAccountId(accountId) },
      signal,
    },
    'Failed to load Journal strategies'
  );
  return normalizeStrategies(response.data);
};

export const fetchJournalAsset = async ({ accountId, assetId, signal } = {}) => {
  if (!assetId) throw new TypeError('asset_id is required.');
  const response = await request(
    {
      method: 'GET',
      url: `/assets/${encodeURIComponent(String(assetId))}/`,
      params: { account_id: requireAccountId(accountId) },
      signal,
    },
    'Failed to load asset metadata'
  );
  return normalizeAssetMetadata(response.data);
};

export const resetDemoDataset = async ({ signal } = {}) => {
  const response = await request({ method: 'POST', url: '/demo/reset/', signal }, 'Failed to reset Demo');
  return normalizeDemoReset(response.data);
};

export default {
  fetchJournalList,
  fetchJournalDetail,
  fetchGuardianAnalysis,
  fetchJournalLedger,
  updateJournalEntry,
  fetchJournalStrategies,
  fetchJournalAsset,
  resetDemoDataset,
};
